/**
 * Game-owned decoration for the six core auth screens.
 *
 * The login, register, forgot-password, reset-password, verify-email and check-email screens
 * are rendered by `frontend/src/core/auth/*` — template files this repo may read and may never
 * edit, because every merge from `t-game/core` has to stay mechanical. So their markup is a
 * fixed input: a `.bg-auth-gradient` stage containing a `.bg-auth-card` form or panel, a `h1`,
 * some inputs and one submit button. Everything this game wants those screens to *look* like
 * therefore has to arrive from outside them, and it arrives two ways:
 *
 *  1. **CSS** (`styles/app.css`, the `html.auth-skin` block) re-skins the core markup itself —
 *     the card, the inputs, the button, the headings, the banners, the links. That is the bulk
 *     of the work and it is deliberately the bulk of the work: CSS needs no hook in the DOM, so
 *     a core refactor that renames a node degrades to "one selector stops matching" instead of
 *     a thrown exception on a live login page.
 *  2. **This module**, for the two things that cannot be a style rule: a route back to the
 *     landing page (core renders no such control, so there was no way out of /login at all), the wordmark, which is an image and needs an element to live in, and the
 *     sound switch, because the menu bed starts on THIS screen and its only other control is
 *     on the Settings screen behind the form.
 *
 * What this costs: the scope is a class on `<html>`, not a wrapper element, because a wrapper
 * would mean moving core's nodes into it and moving them back on teardown — and the one thing
 * a decorator must guarantee is that `app` is exactly what core left behind once it disposes,
 * so the next route's `render*` writes into virgin ground. Nothing here mutates a core node's
 * attributes for the same reason. The back plate is appended to `<body>`, not into `app`, so it
 * is not in the markup core will overwrite and not in the tree teardown has to restore.
 *
 * Every string here goes through `t()`. The one exception is the wordmark's `alt`: "Black Line"
 * is the game's name, and a name is not translated.
 */
import { t } from '../i18n';
import { icon, openModal, plateChrome } from '../shell/ui';
import { mountSoundToggle } from '../shell/sound-toggle';
import { fetchCompanyInfo, fetchLegalPage, fillCompanyVars, type CompanyInfo } from '../shell/legal-api';

/** The one class every override in `app.css` hangs off. Removed on dispose. */
const SCOPE_CLASS = 'auth-skin';

/** 1580x308 transparent PNG; sized down by CSS, but the attributes below reserve its box. */
const WORDMARK_SRC = '/game/ui/logo-blackline.png';
const WORDMARK_W = 1580;
const WORDMARK_H = 308;

export interface AuthSkinOptions {
  /**
   * Draw the back-to-landing plate. Default true.
   *
   * `false` for the three screens where "back" has no meaning: `/verify-email` and
   * `/reset-password` are opened from a link in an email (there is no page behind them), and
   * `/check-email` belongs to a half-signed-in player whose only two exits are the mailbox and
   * the sign-out button core already renders. The caller decides; this only honours the flag.
   */
  back?: boolean;
}

/**
 * Decorates whatever a core `render*` call just put into `app`, and returns a disposer.
 *
 * Call it immediately after the core render, and call the disposer before the next route
 * renders. Safe to call on an `app` that holds no core auth markup at all — it adds nothing
 * and hands back a working no-op, which is what keeps a future core refactor from turning a
 * missing selector into a white screen.
 */
export function decorateAuthScreen(
  app: HTMLElement,
  navigate: (to: string) => void,
  opts: AuthSkinOptions = {},
): () => void {
  const stage = app.querySelector<HTMLElement>('.bg-auth-gradient');
  const card = stage?.querySelector<HTMLElement>('.bg-auth-card') ?? null;
  // No stage or no card means this is not a core auth screen (or core stopped emitting those
  // hooks). Decorating half of it would look worse than not decorating it.
  if (!stage || !card) return () => {};

  const root = document.documentElement;
  // Only unset the scope on dispose if this call is what set it — a nested or double decorate
  // must not strip the skin from the screen that is still up.
  const scopeWasOurs = !root.classList.contains(SCOPE_CLASS);
  root.classList.add(SCOPE_CLASS);

  // ── The wordmark, above the card ──────────────────────────────────────────────────────────
  // Prepended into the stage rather than into the card: core's stage is `grid
  // place-items-center`, so a second child simply becomes a second centred row above the form
  // (the row gap comes from CSS). Putting it inside the card would have meant it scrolled and
  // wrapped with the form fields, and the point of it is to identify the game before the form
  // does — it belongs to the page, not to the panel.
  const brand = document.createElement('div');
  brand.dataset.authSkin = 'brand';

  const mark = document.createElement('img');
  mark.src = WORDMARK_SRC;
  mark.alt = 'Black Line';
  mark.width = WORDMARK_W;
  mark.height = WORDMARK_H;
  mark.decoding = 'async';
  // Above the fold and it is the page's identity, so it is never lazy.
  mark.loading = 'eager';
  mark.draggable = false;

  const strap = document.createElement('p');
  strap.dataset.authSkin = 'strapline';
  // In the landing page's register: nothing here is a claim, every word is a fact about the
  // build. 60Hz is `TICK_RATE`, the browser part is the whole product.
  strap.textContent = t('auth.strapline');

  brand.append(mark, strap);
  stage.prepend(brand);

  // ── The way out ───────────────────────────────────────────────────────────────────────────
  // `navigate`, never `location.href`: a full document load throws away the in-memory access
  // token, so a player who clicked Back would land on the landing page signed out.
  let back: HTMLButtonElement | null = null;
  let onBack: ((event: MouseEvent) => void) | null = null;

  if (opts.back !== false) {
    back = document.createElement('button');
    back.type = 'button';
    back.setAttribute('aria-label', t('auth.backToHome'));

    // A fragment, not a wrapper span: `plateChrome` puts whatever it is given straight into the
    // `.plate-key-label` flex row, which already supplies the gap between the picture and the word.
    const label = document.createDocumentFragment();
    label.append(icon('prev', { size: 24 }), document.createTextNode(t('common.back')));

    // The shell's own control, so this reads as the same kit as every button in the game —
    // `md` is the 44px face, which is also the tap-target floor.
    plateChrome(back, label, { variant: 'secondary', size: 'md' });
    // After plateChrome: it rewrites `className` and `data-plate`, and leaves other attributes
    // alone, but ordering it this way means a future change there cannot drop our CSS hook.
    back.dataset.authSkin = 'back';

    onBack = (event: MouseEvent) => {
      event.preventDefault();
      navigate('/');
    };
    back.addEventListener('click', onBack);

    // `<body>`, not `app`: fixed to the viewport corner, outside the tree core owns and outside
    // the tree the disposer has to hand back untouched.
    document.body.append(back);
    // Lets the stage reserve headroom for it, so the plate never lands on the wordmark at 320px.
    root.dataset.authBack = '1';
  }

  // ── The sound switch ──────────────────────────────────────────────────────────────────────
  // This is the screen the menu bed actually starts on (`main.ts` `ensureAuthMusic`), and until
  // now the only control over it was Settings → Game → Menu music — behind this very form. So
  // it mounts here, on all six screens, including the three that get no back plate: a player
  // who arrived from a verification email hears the same music and has the same right to stop
  // it. Top-right rather than literally beside the back plate, because the plate's corner comes
  // from a rule in `app.css` (`html.auth-skin [data-auth-skin="back"]`, fixed top-left) and
  // pinning a second control to a CSS-owned offset is a collision waiting to happen; the other
  // end of the same row is free on every screen and needs no CSS at all.
  // The register screen alone gets a consent line (2026-09-11): a checkbox that must be ticked
  // before "Create account" is pressable, with the Terms of Service and Privacy Policy opening
  // in the same modal the landing footer uses. Injected from here rather than core for the
  // same reason everything else in this file is — `register-screen.ts` is upstream's.
  const disposeConsent = card.id === 'register-form' ? mountConsent(card) : () => {};
  const disposeAltcha = card.id === 'register-form' ? unautoAltcha(card) : () => {};

  const soundHost = document.createElement('div');
  soundHost.dataset.authSkin = 'sound';
  soundHost.className =
    'fixed z-40 flex items-center top-[max(0.75rem,env(safe-area-inset-top))] '
    + 'right-[max(0.75rem,env(safe-area-inset-right))]';
  document.body.append(soundHost);
  const disposeSound = mountSoundToggle(soundHost);

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    brand.remove();
    disposeAltcha();
    disposeConsent();
    disposeSound();
    soundHost.remove();
    if (back) {
      if (onBack) back.removeEventListener('click', onBack);
      back.remove();
    }
    delete root.dataset.authBack;
    if (scopeWasOurs) root.classList.remove(SCOPE_CLASS);
  };
}

/**
 * Makes the ALTCHA box on the register form start empty.
 *
 * Core mounts it as `<altcha-widget auto="onload">`, which solves the proof of work by itself
 * and draws a ticked "Verified" checkbox before the player has typed anything. Reported as a
 * pre-ticked consent box, and that is exactly how it reads: the one control on the form that is
 * already on when the form opens sits directly above the one the player really must tick.
 *
 * Turning it to `off` is enough, and the timing is not luck. The widget's auto-verify is a
 * reactive effect that arms a `setTimeout(…, 1)`; any other value makes the effect re-run and
 * clear its own timer, and this runs synchronously in the same task as core's render, so the
 * timer physically cannot have fired yet. Nothing is reset afterwards, because nothing started.
 * Ticking the box still verifies — that is the widget's own click path, untouched.
 *
 * Register only, although four core screens carry the widget. On this one the auto-solved box
 * sits directly above the consent box a player really must tick, and the two read as one pair of
 * checkboxes with the top one already on. On the sign-in and password screens the same widget is
 * an invisible captcha nobody is asked to look at, and making it manual there would buy a second
 * click and an error message for anyone who types their password faster than the proof of work.
 *
 * The old value goes back on dispose. It is rewritten in place rather than removed and re-added,
 * because the dispose invariant this module keeps is the card's serialised HTML, and a removed
 * attribute comes back last in the attribute list.
 */
function unautoAltcha(card: HTMLElement): () => void {
  const widget = card.querySelector('altcha-widget');
  if (!widget) return () => {};
  const auto = widget.getAttribute('auto');
  if (auto === null || auto === 'off') return () => {};
  widget.setAttribute('auto', 'off');
  return () => { widget.setAttribute('auto', auto); };
}

/** The two CMS legal documents the consent line names, by the slugs the seed and the shelf pin. */
const CONSENT_DOCS = [
  { slug: 'terms-of-service', labelKey: 'auth.terms' },
  { slug: 'privacy-policy', labelKey: 'auth.privacy' },
] as const;

/**
 * "I agree to the Terms of Service and the Privacy Policy." above the register form's submit,
 * which stays disabled until the box is ticked. The documents open in a modal rather than a
 * new tab: a player mid-registration who leaves the page loses the ALTCHA solve and every
 * field, and comes back to a form that forgot them.
 *
 * Core re-enables the button itself after a failed request (`btn.disabled = false`), which is
 * the right thing whenever the box is still ticked — it cannot be unticked while the request
 * is in flight, because nothing here handles `change` on a disabled form, and the form is not
 * disabled. Returns a disposer that puts the form back exactly as it was: the block gone and
 * the button's `disabled` attribute cleared, so the dispose invariant the rest of this module
 * keeps ("app byte-identical") still holds on the register screen.
 */
function mountConsent(form: HTMLElement): () => void {
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!submit) return () => {};

  const block = document.createElement('label');
  block.dataset.authSkin = 'consent';
  block.className = 'flex items-start gap-3 mb-4 cursor-pointer select-none';

  const box = document.createElement('input');
  box.type = 'checkbox';
  box.name = 'terms';
  box.dataset.authSkin = 'consent-box';
  box.setAttribute('aria-describedby', 'register-consent-text');

  const text = document.createElement('span');
  text.id = 'register-consent-text';
  text.dataset.authSkin = 'consent-text';
  text.className = 'text-[13px] leading-snug text-text-dim pt-0.5';

  let company: CompanyInfo | null = null;
  void fetchCompanyInfo().then((info) => { company = info; }).catch(() => {});

  const docLink = (slug: string, label: string): HTMLButtonElement => {
    const link = document.createElement('button');
    link.type = 'button';
    link.dataset.authSkin = `consent-${slug}`;
    link.className = 'inline text-cyan underline underline-offset-2 decoration-cyan/60 hover:text-cyan-bright font-semibold';
    link.textContent = label;
    link.addEventListener('click', (event) => {
      // The button sits inside the checkbox's label: without this, opening a document would
      // also toggle the box, and a player reading the terms would end up having "agreed".
      event.preventDefault();
      event.stopPropagation();
      let close: () => void = () => {};
      const body = document.createElement('div');
      body.className =
        'flex flex-col gap-3 text-[14px] leading-relaxed text-text [&_h2]:font-heading [&_h2]:uppercase ' +
        '[&_h2]:text-[18px] [&_h2]:mt-4 [&_a]:text-cyan [&_ul]:pl-5 [&_li]:list-disc';
      body.textContent = t('common.loading');
      close = openModal({ title: label, content: [body], onClose: () => close(), width: 'lg' });
      void fetchLegalPage(slug)
        .then((doc) => { body.innerHTML = fillCompanyVars(doc.content, company); })
        .catch(() => { body.textContent = t('landing.footer.docFailed'); });
    });
    return link;
  };

  text.append(
    document.createTextNode(t('auth.consentBefore')),
    docLink(CONSENT_DOCS[0].slug, t(CONSENT_DOCS[0].labelKey)),
    document.createTextNode(t('auth.consentBetween')),
    docLink(CONSENT_DOCS[1].slug, t(CONSENT_DOCS[1].labelKey)),
    document.createTextNode(t('auth.consentAfter')),
  );
  block.append(box, text);
  form.insertBefore(block, submit);

  const sync = (): void => { submit.disabled = !box.checked; };
  box.addEventListener('change', sync);
  sync();

  return () => {
    box.removeEventListener('change', sync);
    block.remove();
    submit.disabled = false;
  };
}
